import { LinkIcon, ShareIcon } from '@heroicons/react/24/outline'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const SocialSharing = ({ recipe, className = '', recipeId }) => {
  const [copied, setCopied] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const buttonRef = useRef(null)
  const dropdownRef = useRef(null)

  // Toggle dropdown
  const toggleDropdown = (e) => {
    e.stopPropagation()
    setShowDropdown(!showDropdown)
  }

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Create a shareable URL for this recipe
  const getShareableUrl = () => {
    try {
      // For recipe detail page, just use the current URL without query params
      if (window.location.pathname.includes('/recipe/')) {
        const url = new URL(window.location.href)
        // Remove any query parameters but keep the path
        return url.origin + url.pathname
      }

      return `${window.location.origin}/recipe/${recipeId}`
    } catch (error) {
      return `${window.location.origin}/recipe/${recipeId}`
    }
  }

  // Handle copying link to clipboard
  const handleCopyLink = (e) => {
    e.stopPropagation()
    navigator.clipboard
      .writeText(getShareableUrl())
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
      .catch((err) => console.error('Failed to copy: ', err))
    setShowDropdown(false)
  }

  // Share handlers
  const handleShareWhatsApp = (e) => {
    e.stopPropagation()
    window.open(
      `https://wa.me/?text=${encodeURIComponent(`Check out this recipe for ${recipe.name}! ${getShareableUrl()}`)}`,
      '_blank'
    )
    setShowDropdown(false)
  }

  const handleShareTwitter = (e) => {
    e.stopPropagation()
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out this recipe for ${recipe.name}!`)}&url=${encodeURIComponent(getShareableUrl())}`,
      '_blank'
    )
    setShowDropdown(false)
  }

  const handleShareFacebook = (e) => {
    e.stopPropagation()
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareableUrl())}`,
      '_blank'
    )
    setShowDropdown(false)
  }

  const renderDropdown = () => {
    if (!showDropdown || !buttonRef.current) return null

    const buttonRect = buttonRef.current.getBoundingClientRect()
    const dropdownHeight = 172 // Height of dropdown
    const dropdownWidth = 192 // Width of dropdown
    const margin = 8 // Margin from edges

    // Check if there's enough space below
    const spaceBelow = window.innerHeight - buttonRect.bottom
    const spaceAbove = buttonRect.top

    // Calculate position
    const dropdownStyle = {
      position: 'fixed',
      maxHeight: '172px',
      width: '192px',
      zIndex: 50,
    }

    // Handle vertical positioning
    if (spaceBelow >= dropdownHeight + margin) {
      // Enough space below - position dropdown below button
      dropdownStyle.top = buttonRect.bottom + margin
    } else if (spaceAbove >= dropdownHeight + margin) {
      // Not enough space below, but enough space above - position dropdown above button
      dropdownStyle.bottom = window.innerHeight - buttonRect.top + margin
    } else {
      // Not enough space above or below - position at the side with more space
      if (spaceBelow >= spaceAbove) {
        dropdownStyle.top = buttonRect.bottom + margin
        dropdownStyle.maxHeight = `${spaceBelow - margin * 2}px`
      } else {
        dropdownStyle.bottom = window.innerHeight - buttonRect.top + margin
        dropdownStyle.maxHeight = `${spaceAbove - margin * 2}px`
      }
    }

    // Handle horizontal positioning
    const spaceRight = window.innerWidth - buttonRect.left
    if (spaceRight >= dropdownWidth + margin) {
      // Enough space to the right - align with button left
      dropdownStyle.left = buttonRect.left
    } else {
      // Not enough space to the right - align to right edge with margin
      dropdownStyle.right = margin
    }

    return createPortal(
      <div
        ref={dropdownRef}
        className="bg-white rounded-md shadow-lg py-1 border border-gray-200"
        style={dropdownStyle}
      >
        <button
          onClick={handleCopyLink}
          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        >
          <LinkIcon className="h-4 w-4 mr-2" />
          {copied ? 'Copied!' : 'Copy link'}
        </button>
        <button
          onClick={handleShareWhatsApp}
          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-2"
            viewBox="0 0 24 24"
            fill="#25D366"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
            <path
              d="M12 .001C5.371.001 0 5.372 0 12.001c0 2.788.952 5.351 2.545 7.388l-1.673 4.98c-.119.355.235.709.59.59l4.98-1.673C8.65 25.049 11.212 26 14 26 20.628 26 26 20.628 26 13.999 26 7.37 20.629 2.001 14 2.001H12zm0 2c6.627 0 12 5.373 12 12s-5.373 12-12 12c-2.658 0-5.11-.862-7.099-2.319l-.507-.343-2.857.961.961-2.857-.343-.507C2.862 17.11 2 14.658 2 12.001c0-6.628 5.372-12 12-12z"
              fillRule="evenodd"
              clipRule="evenodd"
            />
          </svg>
          WhatsApp
        </button>
        <button
          onClick={handleShareTwitter}
          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-2"
            viewBox="0 0 24 24"
            fill="#1DA1F2"
          >
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
          Twitter/X
        </button>
        <button
          onClick={handleShareFacebook}
          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-2"
            viewBox="0 0 24 24"
            fill="#1877F2"
          >
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
          Facebook
        </button>
      </div>,
      document.body
    )
  }

  return (
    <div
      className={`relative ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <ShareIcon
        className="h-5 w-5 text-green-600 group-hover:scale-110 transition-transform duration-150"
        onClick={toggleDropdown}
        ref={buttonRef}
      />
      {renderDropdown()}
    </div>
  )
}

export default SocialSharing
